/**
 * Bewegungs-Primitive für OS:IA.
 *
 * Die Techniken stammen aus react-bits (SpotlightCard, GlareHover, CountUp),
 * sind hier aber ohne dessen Abhängigkeiten nachgebaut: react-bits zieht für
 * ShinyText, CountUp und AnimatedContent `motion/react` beziehungsweise `gsap`
 * heran. Für eine Maske mit tausenden Tabellenzeilen wäre das falsch — die
 * Effekte selbst brauchen nur Zeigerposition und requestAnimationFrame.
 */
import React, { useCallback, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Spotlight
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lichtkegel, der dem Zeiger folgt.
 *
 * react-bits hält die Position im React-State und rendert bei jeder
 * Mausbewegung neu. Auf einem Dashboard mit dreißig Karten sind das dreißig
 * Renderzyklen pro Frame. Hier wandert die Position stattdessen als
 * CSS-Variable direkt an den Knoten: kein Rerender, die Kompositor-Ebene
 * erledigt den Rest.
 */
const useSpotlight = <T extends HTMLElement>() => {
    const ref = useRef<T>(null);

    const onPointerMove = useCallback((e: React.PointerEvent<T>) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--spot-x', `${e.clientX - r.left}px`);
        el.style.setProperty('--spot-y', `${e.clientY - r.top}px`);
    }, []);

    const onPointerEnter = useCallback(() => {
        ref.current?.style.setProperty('--spot-o', '1');
    }, []);

    const onPointerLeave = useCallback(() => {
        ref.current?.style.setProperty('--spot-o', '0');
    }, []);

    return { ref, spotlightProps: { onPointerMove, onPointerEnter, onPointerLeave } };
};

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    /** Deckkraft des Kegels, 0..1. Auf dichten Listen niedriger ansetzen. */
    intensity?: number;
}

export const SpotlightCard: React.FC<SpotlightCardProps> = ({
    children, className = '', intensity = 0.9, style, ...rest
}) => {
    const { ref, spotlightProps } = useSpotlight<HTMLDivElement>();
    return (
        <div
            ref={ref}
            {...spotlightProps}
            className={`spotlight ${className}`}
            style={{ ...style, ['--spot-i' as string]: intensity }}
            {...rest}
        >
            {children}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CountUp
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Zahl, die auf ihren Wert hochläuft.
 *
 * Geschrieben wird direkt in den Textknoten statt über setState — eine
 * Kennzahlenleiste mit acht Werten würde sonst 60-mal pro Sekunde acht
 * Komponenten neu rendern.
 *
 * Die Kurve ist ein easeOutExpo: der Wert ist nach einem Drittel der Zeit
 * schon fast am Ziel und kriecht dann heran. Das liest sich als „zählt",
 * während lineares Hochzählen wie ein Ladebalken wirkt.
 */
export const CountUp: React.FC<{
    to: number;
    duration?: number;
    decimals?: number;
    className?: string;
}> = ({ to, duration = 900, decimals = 0, className = '' }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const fromRef = useRef(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const fmt = (v: number) =>
            v.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

        // Wer Bewegung reduziert haben will, bekommt den Endwert sofort.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            el.textContent = fmt(to);
            fromRef.current = to;
            return;
        }

        const from = fromRef.current;
        const delta = to - from;
        if (delta === 0) { el.textContent = fmt(to); return; }

        let raf = 0;
        let start: number | null = null;

        const step = (t: number) => {
            if (start === null) start = t;
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(2, -10 * p);
            el.textContent = fmt(from + delta * (p === 1 ? 1 : eased));
            if (p < 1) raf = requestAnimationFrame(step);
            else fromRef.current = to;
        };

        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [to, duration, decimals]);

    return <span ref={ref} className={`tabular ${className}`}>0</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Magnet
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Element, das sich dem Zeiger entgegenneigt.
 *
 * Nur für einzelne, bewusst gesetzte Ziele — der Effekt verliert sofort seine
 * Wirkung, wenn er auf jedem Knopf sitzt, und macht dichte Masken unruhig.
 */
export const Magnetic: React.FC<{
    children: React.ReactNode;
    /** Maximaler Ausschlag in Pixeln. */
    strength?: number;
    className?: string;
}> = ({ children, strength = 6, className = '' }) => {
    const ref = useRef<HTMLDivElement>(null);
    // Die Abfrage steht in einem Ref statt im State: sie loest keine Neuanzeige
    // aus, und der Wert wird erst gebraucht, wenn der Zeiger das Element
    // erreicht — vorher gibt es nichts zu entscheiden.
    const reducedRef = useRef<boolean | null>(null);

    const move = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = ref.current;
        if (!el) return;
        if (reducedRef.current === null) {
            reducedRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
        if (reducedRef.current) return;
        const r = el.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        el.style.transform = `translate3d(${dx * strength}px, ${dy * strength}px, 0)`;
    };

    const reset = () => {
        const el = ref.current;
        if (el) el.style.transform = 'translate3d(0,0,0)';
    };

    return (
        <div
            ref={ref}
            onPointerMove={move}
            onPointerLeave={reset}
            className={`will-change-transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${className}`}
        >
            {children}
        </div>
    );
};
